import type { JSX } from 'react';
import { TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClickableTableRow } from '@/shared/components/ClickableTableRow';
import type { Company } from '../types/company.types';
import { CompanyLogo } from './CompanyLogo';
import { CompanyActions } from './CompanyActions';
import { sizeLabels, statusConfig } from '../utils/company-row.utils';

export interface CompanyRowProps {
  company: Company;
  onEdit: (c: Company) => void;
  onActivate?: ((id: string) => void) | undefined;
  onSuspend?: ((id: string) => void) | undefined;
  onEnterCompany?: ((c: Company) => void) | undefined;
  showStatusActions: boolean;
}

export function CompanyRow({
  company,
  onEdit,
  onActivate,
  onSuspend,
  onEnterCompany,
  showStatusActions,
}: CompanyRowProps): JSX.Element {
  const { variant, label } = statusConfig[company.status];
  const location = [company.city, company.country].filter(Boolean).join(', ');

  return (
    <ClickableTableRow
      onActivate={() => onEnterCompany?.(company)}
      label={company.name}
      testId={`company-row-${company.id}`}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <CompanyLogo company={company} />
          <div>
            <button
              onClick={() => onEnterCompany?.(company)}
              className="font-medium hover:text-primary hover:underline text-left"
              data-testid={`company-name-${company.id}`}
            >
              {company.name}
            </button>
            <p className="text-sm text-muted-foreground">{company.email}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>{company.industry || '-'}</TableCell>
      <TableCell>{location || '-'}</TableCell>
      <TableCell>{company.companySize ? sizeLabels[company.companySize] : '-'}</TableCell>
      <TableCell>
        <Badge variant={variant}>{label}</Badge>
      </TableCell>
      <TableCell className="text-right">
        <CompanyActions
          company={company}
          onEdit={onEdit}
          onSuspend={onSuspend}
          onActivate={onActivate}
          showStatusActions={showStatusActions}
        />
      </TableCell>
    </ClickableTableRow>
  );
}
